// src/modules/app-views/_apps/appmulti/quotations/Components.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { forwardRef, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Flex,
  Form,
  InputNumber,
  Row,
  Statistic,
  Steps,
  Switch,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ShoppingCartOutlined,
  TagOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { FaPlaneArrival, FaPlaneDeparture } from 'react-icons/fa';
import { MdOutlineShoppingCart } from 'react-icons/md';
import { AiOutlineSafetyCertificate } from 'react-icons/ai';
import { BsPersonPlusFill } from 'react-icons/bs';
import dayjs from 'dayjs';
import { toCapitalCase, downloadFile } from 'utils/helpers';
import translateX from 'utils/translateX';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { DEFAULT_BACK_COLOR, DEFAULT_BACK_COLOR_DARK } from 'constants/Constants';
import {
  SimpleFormProvider,
  useSimpleFormContext,
} from 'components/SimpleComponents/SimpleFormProvider';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleButton from 'components/SimpleComponents/SimpleButton';
import Icon from 'components/JarvislyComponents/Icon';
import appmultiService from 'modules/app-views/_apps/appmulti/services/appmultiService';
import financialService from 'services/financialService';
import appService from 'services/appService';
import { getQuery, updateQuery } from 'utils/urlUtils';
import useZustand from 'hooks/useZustand';
import MultiQuotationCoveragesModal from './List/Modals/CoveragesModal';
import MultiQuotationPriceAdjustmentModal from './Form/Steps/Modals/PriceAdjustmentModal';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const defaultAge = 21;
const minAge = 1;
const maxAge = 99;

const passengerLabelStyle = { marginBottom: 0, color: 'rgb(114, 132, 154)' };
const passengerIdxStyle = { marginTop: 18 };
const passengerAgeLabel = { marginBottom: 0, marginRight: '.5rem', marginLeft: '.5rem' };
const passengerColStyle = { display: 'flex', flexDirection: 'column' };
const passengerRemoveColStyle = { height: '65px', display: 'flex', alignItems: 'flex-end' };
const passengerFlexStyle = { height: 78 };
const dropdownBtnStyle = {
  padding: 8,
  width: 40,
  height: 40,
  marginTop: 4,
  marginLeft: -40,
  fontSize: '18px',
};
const passengersInputStyle = {
  textAlign: 'left',
  color: 'var(--ant-color-text)',
  WebkitTextFillColor: 'var(--ant-color-text)',
};

// *******************************************************************************
// COMPONENT — MultiQuotationsHeader (outer forwardRef wrapper)
// *******************************************************************************
export const MultiQuotationsHeader = forwardRef((props, ref) => {
  const { document } = props;
  const storedCurrentTheme = useZustand('theme', 'currentTheme');
  const bgColor = storedCurrentTheme === 'dark' ? DEFAULT_BACK_COLOR_DARK : DEFAULT_BACK_COLOR;
  return (
    <div
      ref={ref}
      style={{ backgroundColor: bgColor, margin: '0 -25px', padding: '0 25px', overflow: 'hidden' }}
    >
      <SimpleFormProvider
        name="form-quotation-header"
        layout="vertical"
        initialValues={document}
        className="mb-3"
      >
        <QuotationsHeaderBody {...props} />
      </SimpleFormProvider>
    </div>
  );
});

MultiQuotationsHeader.displayName = 'MultiQuotationsHeader';

// QuotationsHeaderBody — inner body: reads form from context, owns handlers/effects
const QuotationsHeaderBody = props => {
  const { isWorking, document, setDocumentContext, setIsWorking, selectedModule } = props;
  const { form } = useSimpleFormContext();

  // STATE
  const [visible, setVisible] = useState(false);
  const [passengerAges, setPassengerAges] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState(null);

  // RENDER HELPERS
  const isDone = document?.status === 'generated_order';
  const isDisabled = isWorking || isDone;
  const disabledDate = current => current && current < dayjs().startOf('day');
  const subscription = useZustand('auth', 'subscription');
  const datePickerRef = useRef(null);

  // EFFECTS
  useEffect(() => {
    (async () => {
      await appmultiService.loadParameters();
      setDestinationOptions(appmultiService.getDestinations());
    })();
  }, []);

  useEffect(() => {
    const q = getQuery();
    const filter = {
      destiny: q?.destiny,
      departure: q?.departure,
      arrival: q?.arrival,
      passengers: q?.passengers,
    };

    if (
      filter?.passengers &&
      typeof filter.passengers === 'string' &&
      filter.passengers.includes(',')
    ) {
      filter.passengers = filter.passengers.split(',');
    }
    if (Array.isArray(filter?.passengers)) {
      filter.passengers = filter.passengers
        .map(p => {
          if (!isNaN(p)) {
            const age = Number(p);
            return age < minAge || age > maxAge ? defaultAge : age;
          }
          return null;
        })
        .filter(p => p);
      if (filter.passengers.length === 0) filter.passengers.push(defaultAge);
    }

    const passengers =
      filter?.passengers && Array.isArray(filter.passengers) && filter.passengers.length > 0
        ? filter?.passengers
        : filter?.passengers && !isNaN(filter.passengers)
          ? [filter.passengers]
          : [defaultAge];

    setPassengerAges([...passengers]);

    if (!destinationOptions) return;

    if (destinationOptions?.findIndex(d => d?.value === filter?.destiny) > -1) {
      form.setFieldValue('destiny', filter.destiny);
    } else {
      if (!isDone) form.setFieldValue('destiny', document?.destiny);
    }

    const travelPeriod = [];

    if (filter?.departure && dayjs(filter.departure, 'YYYY-MM-DD').isValid()) {
      travelPeriod.push(dayjs(filter.departure, 'YYYY-MM-DD').locale('pt-br'));
    } else {
      if (document?.__travelPeriod?.[0] && !isDone) {
        travelPeriod.push(dayjs(document.__travelPeriod[0], 'YYYY-MM-DD').locale('pt-br'));
      }
    }

    if (filter?.arrival && dayjs(filter.arrival, 'YYYY-MM-DD').isValid()) {
      travelPeriod.push(dayjs(filter.arrival, 'YYYY-MM-DD').locale('pt-br'));
    } else {
      if (document?.__travelPeriod?.[1] && !isDone) {
        travelPeriod.push(dayjs(document.__travelPeriod[1], 'YYYY-MM-DD').locale('pt-br'));
      }
    }

    const departure = travelPeriod?.[0];
    const arrival = travelPeriod?.[1];

    const days = departure && arrival ? dayjs(arrival).diff(departure, 'days') + 1 : 0;

    form.setFieldValue('__travelPeriod', travelPeriod);
    form.setFieldValue('days', days);

    setTimeout(async () => await filterPlans(passengers), 600);
  }, [form, document, subscription, destinationOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync passenger count to form field after re-render (Form.Item must be registered first in AntD v6)
  useEffect(() => {
    if (passengerAges.length > 0) {
      form.setFieldValue('passengers', passengerAges.length);
    }
  }, [form, passengerAges]); // eslint-disable-line react-hooks/exhaustive-deps

  // HANDLERS
  const onChangeTravelPeriod = async value => {
    if (!value) {
      form.setFieldValue('__travelPeriod', []);
      form.setFieldValue('days', 0);
    } else {
      const days = dayjs(value[1]).diff(value[0], 'days') + 1;
      form.setFieldValue('__travelPeriod', value);
      form.setFieldValue('days', days);
    }
    await filterPlans();
  };

  const filterPlans = async travellers => {
    setIsWorking(true);
    try {
      const destiny = form.getFieldValue('destiny') || null;
      if (!destiny) return;

      const travelPeriodArr = form.getFieldValue('__travelPeriod');
      const departure = travelPeriodArr?.[0]?.format('YYYY-MM-DD') || null;
      const arrival = travelPeriodArr?.[1]?.format('YYYY-MM-DD') || null;
      const passengers = Array.isArray(travellers) ? travellers : passengerAges;

      updateQuery({ destiny, departure, arrival, passengers }, true);

      const days =
        departure && arrival ? dayjs(travelPeriodArr[1]).diff(travelPeriodArr[0], 'days') + 1 : 0;

      if (isDone) return;

      const data = await appmultiService.getPlans(destiny, travelPeriodArr, passengers);

      setDocumentContext({
        listPlans: data,
        filter: { destiny, travelPeriodArr, departure, arrival, passengers, days },
      });
    } finally {
      setIsWorking(false);
    }
  };

  const handleAgeChange = async (passengerIdx, value) => {
    passengerAges[passengerIdx] = value === null ? 0 : value;
    setPassengerAges([...passengerAges]);
  };

  const onClickAddPassenger = async () => {
    setPassengerAges([...passengerAges, defaultAge]);
  };

  const remove = async idx => {
    const updated = passengerAges.filter((_, i) => i !== idx);
    setPassengerAges(updated);
  };

  const handleVisibleChange = async flag => {
    setVisible(flag);
    if (!flag) setTimeout(async () => await filterPlans(passengerAges), 400);
  };

  // RENDER
  const DropdownPassengers = () => (
    <div className="dropdown-content">
      {passengerAges.map((p, idx) => (
        <Row key={idx} align="middle" gutter={ROW_GUTTER} className="mb-2">
          <Flex vertical justify="flex-start" style={passengerFlexStyle}>
            <div style={passengerLabelStyle}>Passageiro</div>
            <div style={passengerIdxStyle} className="text-center font-weight-bold">
              {idx + 1}
            </div>
          </Flex>
          <Col style={passengerColStyle}>
            <p style={passengerAgeLabel}>Idade</p>
            <InputNumber
              className="m-2"
              min={1}
              max={99}
              placeholder="Idade"
              value={passengerAges[idx]}
              onChange={value => handleAgeChange(idx, value)}
            />
          </Col>
          <Col style={passengerRemoveColStyle}>
            <Button
              icon={<MinusCircleOutlined />}
              disabled={passengerAges.length === 1}
              onClick={() => remove(idx)}
            />
          </Col>
        </Row>
      ))}
      <Row justify="center" align="middle">
        <Button type="primary" className="ml-2 mr-2" onClick={onClickAddPassenger}>
          <PlusOutlined />
          <span>Passageiro</span>
        </Button>
      </Row>
    </div>
  );

  return (
    <>
      <Row gutter={ROW_GUTTER} className="mt-3">
        <Col xs={24} md={8}>
          <SimpleSelect
            name="destiny"
            label="destiny"
            options={destinationOptions}
            onChange={filterPlans}
            disabled={isDisabled}
            required
          />
        </Col>

        <Col xs={24} md={4}>
          <Row align="middle" className="flex-nowrap">
            <SimpleNumber
              label="passengers_and_ages"
              name="passengers"
              disabled={true}
              className="w-100"
              style={passengersInputStyle}
              onChange={filterPlans}
              min={1}
              max={20}
              required
            />
            <Tooltip title={translateX('inform_passengers_and_ages')}>
              <Dropdown
                popupRender={DropdownPassengers}
                onOpenChange={handleVisibleChange}
                placement="bottom"
                open={visible}
                trigger={['click']}
                arrow
              >
                <Button style={dropdownBtnStyle} icon={<BsPersonPlusFill />} />
              </Dropdown>
            </Tooltip>
          </Row>
        </Col>

        <Col xs={20} md={9}>
          <div ref={datePickerRef}>
            <Form.Item name="__travelPeriod" label={translateX('travel_period')} required>
              <DatePicker.RangePicker
                className="w-100"
                disabled={isDisabled}
                onChange={onChangeTravelPeriod}
                disabledDate={disabledDate}
                format="DD/MM/YYYY"
                allowEmpty={[true, true]}
              />
            </Form.Item>
          </div>
        </Col>

        <Col xs={4} md={3}>
          <SimpleNumber name="days" disabled={true} className="w-100" />
        </Col>
      </Row>
    </>
  );
};

// *******************************************************************************
// COMPONENT — PlanComparatorSwitch (private)
// *******************************************************************************
const comparatorAvatarBorderStyle = color => ({ border: `1px ${color || 'darkcyan'} solid` });
const comparatorHeaderFlexStyle = { marginBottom: 14 };
const comparatorTagsFlexStyle = { display: 'flex', gap: '8px', marginTop: 12 };
const comparatorPriceColStyle = { flexDirection: 'column' };
const comparatorPriceValueStyle = { display: 'none' };

const PlanComparatorSwitch = props => {
  const {
    plan,
    setCompare,
    compare,
    setCompareColumns,
    setCompareData,
    comparePlans,
    setComparePlans,
    setSelectedRecord,
    setShowCompareModal,
    onClickOpenCheckoutModal,
  } = props;

  // HANDLERS
  const onDownloadFile = item => {
    if (!item?.coveragesFileUrl || !item?.name) return;
    const fileName = `COBERTURAS ${item?.name.toUpperCase()}.pdf`;
    downloadFile(item.coveragesFileUrl, fileName);
  };

  const onMoreInfo = item => {
    if (!item?.coverageAdditionalInfo) return;
    appService.modal({ flag: 'i', title: 'Info', content: item.coverageAdditionalInfo });
  };

  const onClickCompare = checked => {
    let plans;

    if (checked) {
      plans = [...comparePlans, plan];
      plans.sort((a, b) => {
        const priceA = a.summary.total?.resellerNetPrice || 0;
        const priceB = b.summary.total?.resellerNetPrice || 0;
        return priceA - priceB;
      });
      setCompare([...compare, { _id: plan?._id, name: plan?.name }]);
    } else {
      plans = comparePlans.filter(p => p?._id !== plan?._id);
      setCompare([...compare.filter(p => p?._id !== plan?._id)]);
    }

    const coveragesList = Array.from(
      new Set([
        'price',
        'make_purchase',
        ...plans.flatMap(item => item.coverages.map(coverage => coverage.coverageKey)),
        'all_coverages',
        'additional_info',
      ])
    );

    const columns = [
      {
        title: translateX('coverages'),
        dataIndex: 'coverageKey',
        width: 400,
        fixed: 'left',
        render: value => translateX(value),
      },
      ...plans.map(p => ({
        title: (
          <>
            <div style={comparatorHeaderFlexStyle}>
              <Avatar
                size={86}
                style={comparatorAvatarBorderStyle(p?.__operator?.backColor)}
                src={p?.__operator?.avatar?.base64Thumb}
              />
            </div>
            <div>
              <span className="text-darkcyan">{p.name}</span>
            </div>
            <Flex justify="center">
              <div style={comparatorTagsFlexStyle}>
                {p?.cheaper ? (
                  <Alert title="MAIS BARATO" type="success" icon={<TagOutlined />} showIcon />
                ) : null}
                {p?.moreExpansive ? (
                  <Alert title="MAIS CARO" type="error" icon={<TagOutlined />} showIcon />
                ) : null}
                {p?.bestSeller ? (
                  <Alert title="MAIS VENDIDO" type="info" icon={<TrophyOutlined />} showIcon />
                ) : null}
              </div>
            </Flex>
          </>
        ),
        dataIndex: p._id,
        align: 'center',
        render: value => {
          if (value === 'price') {
            const resellerGrossPrice = p?.summary?.total?.resellerGrossPrice;
            const resellerNetPrice = p?.summary?.total?.totalToPay;
            const providerNetPrice = p?.summary?.total?.providerNetPrice;
            const valueWithDiscount = financialService.discount(resellerNetPrice, 5, 2, true);

            return (
              <Flex justify="center" style={comparatorPriceColStyle}>
                <Statistic
                  className="price-from-to text-center"
                  title={`DE: ${financialService.formatMoney({
                    value: resellerGrossPrice,
                    localeSymbol: 'brl',
                    resultToStr: true,
                  })}`}
                  value={`${financialService.formatMoney({
                    value: resellerNetPrice,
                    localeSymbol: 'brl',
                    resultToStr: true,
                  })}`}
                  prefix="POR:"
                />
                <div>em 6X cartão ou {valueWithDiscount} no pix</div>
                <Statistic
                  className="custom-statistic price-from-to text-center"
                  title={`${translateX('operator_cost')}: ${financialService.formatMoney({
                    value: providerNetPrice,
                    localeSymbol: 'brl',
                    resultToStr: true,
                  })}`}
                  styles={{ content: comparatorPriceValueStyle }}
                />
              </Flex>
            );
          } else if (value === 'make_purchase') {
            return (
              <Flex justify="center">
                <Button
                  size="large"
                  type="primary"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => {
                    setSelectedRecord(p);
                    setShowCompareModal(false);
                    onClickOpenCheckoutModal(p);
                  }}
                >
                  Comprar
                </Button>
              </Flex>
            );
          } else if (value === 'all_coverages') {
            return (
              <Flex justify="center">
                {p?.coveragesFileUrl ? (
                  <Button type="link" icon={<DownloadOutlined />} onClick={() => onDownloadFile(p)}>
                    Download
                  </Button>
                ) : (
                  '-'
                )}
              </Flex>
            );
          } else if (value === 'additional_info') {
            return (
              <Flex justify="center">
                {p?.coverageAdditionalInfo ? (
                  <Button type="default" icon={<PlusOutlined />} onClick={() => onMoreInfo(p)}>
                    Info
                  </Button>
                ) : (
                  '-'
                )}
              </Flex>
            );
          } else {
            return value ? value : '-';
          }
        },
      })),
    ];

    const dataSource = coveragesList.map(coverageKey => {
      const rowData = { key: coverageKey, coverageKey };
      plans.forEach(item => {
        const coverage = item.coverages.find(cov => cov.coverageKey === coverageKey);
        if (coverageKey === 'price') {
          rowData[item._id] = 'price';
        } else if (coverageKey === 'make_purchase') {
          rowData[item._id] = 'make_purchase';
        } else if (coverageKey === 'all_coverages') {
          rowData[item._id] = 'all_coverages';
        } else if (coverageKey === 'additional_info') {
          rowData[item._id] = 'additional_info';
        } else {
          rowData[item._id] = coverage ? appmultiService.getCoverageValue(coverage, true) : '-';
        }
      });
      return rowData;
    });

    setComparePlans([...plans]);
    setCompareColumns([...columns]);
    setCompareData([...dataSource]);

    return checked;
  };

  // RENDER
  const isChecked = !!compare.find(p => p?._id === plan?._id);

  return (
    <>
      <Switch
        className="mr-3"
        checkedChildren={<CheckOutlined />}
        unCheckedChildren={<CloseOutlined />}
        checked={isChecked}
        onChange={onClickCompare}
      />
      Comparar Plano
    </>
  );
};

// *******************************************************************************
// COMPONENT — CardPlan (listing + checkout profiles)
// *******************************************************************************
const cardTagsStyle = { display: 'flex', gap: '8px', marginTop: -40, marginBottom: 12 };
const cardColLeftStyle = { display: 'flex', flexDirection: 'column', height: 'auto' };
const cardPriceColStyle = { display: 'flex', flexDirection: 'column', height: 'auto' };
const cardPriceValueHidden = { display: 'none' };
const priceAdjustBtnStyle = { width: 180 };

export const CardPlan = props => {
  const {
    plan,
    total = props?.plan?.summary?.total,
    highlights = props?.plan?.highlights,
    onClickOpenCheckoutModal,
    filter,
    profile = 'listing',
    strongBorder = true,
    setSelectedRecord,
    title,
  } = props;

  // STATE
  const [showCoveragesModal, setShowCoveragesModal] = useState(false);

  // REFS
  const priceAdjustmentModalRef = useRef(null);
  const openPriceAdjustmentModal = () => priceAdjustmentModalRef.current?.setShowModal(true);

  // RENDER HELPERS
  const isMobile = appService.isMobile();
  const resellerNetPrice = total?.resellerNetPrice || 0;
  const adjustedPrice = total?.totalAdjusted || 0;
  const isDiff = adjustedPrice !== resellerNetPrice;

  const avatarBorderStyle = { border: `1px ${plan?.__operator?.backColor || 'darkcyan'} solid` };
  const priceActionsFlexDir = { flexDirection: !isMobile ? 'row' : 'column-reverse' };

  // RENDER
  return (
    <>
      <MultiQuotationCoveragesModal
        showModal={showCoveragesModal}
        setShowModal={setShowCoveragesModal}
        record={plan}
        setSelectedRecord={setSelectedRecord}
      />

      <Card
        variant="outlined"
        title={title}
        hoverable={profile === 'listing'}
        key={plan?._id}
        className={`pl-2 pr-2 w-100 ${strongBorder ? 'strong-border' : ''}`}
      >
        {profile !== 'checkout' ? (
          <Flex justify="flex-end">
            <div style={cardTagsStyle}>
              {plan?.cheaper ? (
                <Alert title="MAIS BARATO" type="success" icon={<TagOutlined />} showIcon />
              ) : null}
              {plan?.moreExpansive ? (
                <Alert title="MAIS CARO" type="error" icon={<TagOutlined />} showIcon />
              ) : null}
              {plan?.bestSeller ? (
                <Alert title="MAIS VENDIDO" type="info" icon={<TrophyOutlined />} showIcon />
              ) : null}
            </div>
          </Flex>
        ) : null}

        <Row gutter={ROW_GUTTER} align="stretch">
          <Col xs={24} md={12} style={cardColLeftStyle}>
            <Flex vertical justify="space-between" className="h-100">
              <Card.Meta
                className="title-without-margin icon-64 brake-title title-risk"
                avatar={
                  <Avatar
                    size={86}
                    style={avatarBorderStyle}
                    src={plan?.__operator?.avatar?.base64Thumb}
                  />
                }
                title={<span className="text-uppercase">{plan?.name}</span>}
                description={
                  <>
                    <div>
                      {plan?.minAge} á {plan?.maxAge} Anos | Mínimo {plan?.minDays} dia
                      {plan?.minDays > 1 ? 's' : ''}
                    </div>
                    <div>{toCapitalCase(plan?.__operator?.name)}</div>
                  </>
                }
              />

              {profile === 'listing' ? (
                <Flex className="mt-4" justify="start">
                  {!isMobile ? <PlanComparatorSwitch {...props} /> : null}
                </Flex>
              ) : filter ? (
                <Flex className="mt-5" justify={isMobile ? 'center' : 'start'}>
                  <div className="d-flex">
                    <div className="mt-1">
                      <Icon type={FaPlaneDeparture} className="mr-2" />
                    </div>
                    <div className="font-weight-bold">
                      {filter?.departure ? dayjs(filter.departure).format('L') : null}
                    </div>
                  </div>
                  <div className="ml-4 d-flex">
                    <div className="mt-1">
                      <Icon type={FaPlaneArrival} className="mr-2" />
                    </div>
                    <div className="font-weight-bold">
                      {filter?.arrival ? dayjs(filter.arrival).format('L') : null}
                    </div>
                  </div>
                </Flex>
              ) : null}
            </Flex>
          </Col>

          <Col xs={24} md={7}>
            <Flex vertical justify="center" className={`h-100 ${isMobile ? 'mt-3' : 'mt-0'}`}>
              <Steps
                className="fit-last-item mt-4 mb-3 mt-md-0 mb-md-0"
                current={99}
                size="small"
                orientation="vertical"
                items={highlights}
              />
            </Flex>
          </Col>

          <Col xs={24} md={5} style={cardPriceColStyle}>
            <Flex vertical justify="space-between" className="h-100">
              <div>
                <Statistic
                  className={`price-from-to text-right ${isDiff ? 'price-from-to-value' : ''}`}
                  title={`DE: ${financialService.formatMoney({
                    value: total?.resellerGrossPrice,
                    localeSymbol: 'brl',
                    resultToStr: true,
                  })}`}
                  value={`${financialService.formatMoney({
                    value: total?.resellerNetPrice,
                    localeSymbol: 'brl',
                    resultToStr: true,
                  })}`}
                  prefix="POR:"
                />
                <Statistic
                  className="custom-statistic price-from-to text-right"
                  title={`${translateX('operator_cost')}: ${financialService.formatMoney({
                    value: total?.providerNetPrice,
                    localeSymbol: 'brl',
                    resultToStr: true,
                  })}`}
                  styles={{ content: cardPriceValueHidden }}
                />
                {isDiff ? (
                  <Statistic
                    className="price-from-to text-right"
                    value={`${financialService.formatMoney({
                      value: total?.totalAdjusted,
                      localeSymbol: 'brl',
                      resultToStr: true,
                    })}`}
                    prefix={translateX('adjusted_price')}
                  />
                ) : null}
              </div>
              <Flex className="mt-4" style={priceActionsFlexDir} justify="end">
                {profile === 'listing' ? (
                  <div className={isMobile ? 'mt-4' : 'mt-0'}>
                    {isMobile ? <PlanComparatorSwitch {...props} /> : null}
                  </div>
                ) : null}

                <div className="text-right">
                  <Tooltip title={translateX('coverages')} placement="bottom">
                    <Button
                      className="ml-2"
                      icon={<AiOutlineSafetyCertificate />}
                      onClick={() => setShowCoveragesModal(true)}
                    />
                  </Tooltip>

                  {profile === 'listing' ? (
                    <Tooltip title={translateX('buy')} placement="bottom">
                      <Button
                        type="primary"
                        className="ml-2"
                        icon={<MdOutlineShoppingCart />}
                        onClick={() => onClickOpenCheckoutModal(plan)}
                      />
                    </Tooltip>
                  ) : null}
                </div>
              </Flex>
            </Flex>
          </Col>
        </Row>

        {profile === 'checkout' ? (
          <Flex justify="flex-end" align="center" className="mt-2">
            <SimpleButton
              title="price_adjustment"
              type="default"
              icon={<EditOutlined />}
              disabled={!!props.order?._id}
              onClick={openPriceAdjustmentModal}
              style={priceAdjustBtnStyle}
            />
            <MultiQuotationPriceAdjustmentModal
              {...props}
              ref={priceAdjustmentModalRef}
              plan={props.plan}
              total={props.plan?.summary?.total}
            />
          </Flex>
        ) : null}
      </Card>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
// Named exports: MultiQuotationsHeader, CardPlan (both declared inline above)
